/**
 * Speed, distance and time: v = d / t, d = v × t, t = d / v — and average speed over a
 * trip made of several legs.
 *
 * Average speed is the TOTAL distance divided by the TOTAL time. It is not the mean of
 * the leg speeds unless every leg takes the same time: driving 60 km at 80 km/h and
 * another 60 km at 40 km/h takes 0.75 h + 1.5 h, so the average is 120 / 2.25 =
 * 53.3 km/h, not 60 km/h.
 *
 * All quantities here are SI (metres, seconds, m/s) — the widget converts at the edges.
 */

export type SdtUnknown = "speed" | "distance" | "time";

export interface SdtKnown {
  distance?: number;
  speed?: number;
  time?: number;
}

export type SdtError =
  | "negative-distance"
  | "negative-speed"
  | "negative-time"
  | "zero-time"
  | "zero-speed";

export type SdtResult =
  | { ok: true; distance: number; speed: number; time: number }
  | { ok: false; error: SdtError };

export function solveSdt(unknown: SdtUnknown, known: SdtKnown): SdtResult {
  const { distance = 0, speed = 0, time = 0 } = known;
  if (unknown !== "distance" && distance < 0) {
    return { ok: false, error: "negative-distance" };
  }
  if (unknown !== "speed" && speed < 0) {
    return { ok: false, error: "negative-speed" };
  }
  if (unknown !== "time" && time < 0) {
    return { ok: false, error: "negative-time" };
  }
  if (unknown === "speed") {
    return time > 0
      ? { ok: true, distance, speed: distance / time, time }
      : { ok: false, error: "zero-time" };
  }
  if (unknown === "time") {
    return speed > 0
      ? { ok: true, distance, speed, time: distance / speed }
      : { ok: false, error: "zero-speed" };
  }
  return { ok: true, distance: speed * time, speed, time };
}

export interface LegInput {
  /** Metres, ≥ 0. */
  distance: number;
  /** What the second number is: the leg's speed (m/s) or its time (s). */
  kind: "speed" | "time";
  value: number;
}

export interface Leg {
  distance: number;
  speed: number;
  time: number;
}

export type TripError =
  | "no-legs"
  | "negative"
  | "zero-speed"
  | "zero-time"
  | "zero-total-time";

export type TripResult =
  | {
      ok: true;
      legs: Leg[];
      totalDistance: number;
      totalTime: number;
      averageSpeed: number;
      /** The plain mean of the leg speeds, for comparison with the true average. */
      meanOfSpeeds: number;
    }
  | { ok: false; error: TripError; leg?: number };

function solveLeg(input: LegInput): Leg | TripError {
  if (input.distance < 0 || input.value < 0) {
    return "negative";
  }
  if (input.kind === "speed") {
    return input.value > 0
      ? {
          distance: input.distance,
          speed: input.value,
          time: input.distance / input.value,
        }
      : "zero-speed";
  }
  if (input.value > 0) {
    return {
      distance: input.distance,
      speed: input.distance / input.value,
      time: input.value,
    };
  }
  // A leg that takes no time covers no distance (it is not a leg at all).
  return "zero-time";
}

export function solveTrip(inputs: readonly LegInput[]): TripResult {
  if (inputs.length === 0) {
    return { ok: false, error: "no-legs" };
  }
  const legs: Leg[] = [];
  for (const [index, input] of inputs.entries()) {
    const leg = solveLeg(input);
    if (typeof leg === "string") {
      return { ok: false, error: leg, leg: index };
    }
    legs.push(leg);
  }
  const totalDistance = legs.reduce((sum, leg) => sum + leg.distance, 0);
  const totalTime = legs.reduce((sum, leg) => sum + leg.time, 0);
  if (!(totalTime > 0)) {
    return { ok: false, error: "zero-total-time" };
  }
  return {
    ok: true,
    legs,
    totalDistance,
    totalTime,
    averageSpeed: totalDistance / totalTime,
    meanOfSpeeds: legs.reduce((sum, leg) => sum + leg.speed, 0) / legs.length,
  };
}

export interface Hms {
  hours: number;
  minutes: number;
  /** Seconds, rounded to hundredths. */
  seconds: number;
}

/** Split a time in seconds into hours, minutes and seconds (rounded to 0.01 s first). */
export function toHms(totalSeconds: number): Hms {
  const hundredths = Math.round(totalSeconds * 100);
  const whole = Math.floor(hundredths / 100);
  return {
    hours: Math.floor(whole / 3600),
    minutes: Math.floor((whole % 3600) / 60),
    seconds: (hundredths - Math.floor(whole / 60) * 6000) / 100,
  };
}

/** Pace as whole seconds per `perMetres` (per km or per mile), rounded to the second. */
export function paceSeconds(speed: number, perMetres: number): number {
  return Math.round(perMetres / speed);
}

/** `m:ss` for a pace in seconds, or `h:mm:ss` from an hour up. */
export function clock(totalSeconds: number): string {
  const rounded = Math.round(totalSeconds);
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const seconds = rounded % 60;
  const two = (n: number) => String(n).padStart(2, "0");
  return hours > 0
    ? `${hours}:${two(minutes)}:${two(seconds)}`
    : `${minutes}:${two(seconds)}`;
}
